import {gql} from '@apollo/client'
import {Flow, FlowGenerator, UseFlowQuery} from 'features/procedures/Flow/Flow'
import {maybeYieldChildcareMethodSelect} from 'features/procedures/Flow/helpers'
import {useQuery} from 'hooks/graphql'

const START_CHILDCARE_FLOW_QUERY = gql`
  query StartChildcareFlowQuery($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        employee {
          id
          healthInsurance {
            id
            joined
          }
          welfarePensionInsurance {
            id
            joined
          }
          employmentInsurance {
            id
            joined
          }
          currentOffice {
            id
            healthInsuranceType
          }
          pastChildcareProcedureStatuses(forProcedureType: start_childcare) {
            id
          }
        }
        childcareMethodSelect: procedureTmpDatum(type: employee_draft, name: "childcare_method_select") {
          id
        }
      }
    }
  }
`

interface FlowQueryData {
  client: {
    id: string
    procedureStatus: {
      id: string
      employee: {
        id: string
        healthInsurance?: {
          id: string
          joined: boolean
        }
        welfarePensionInsurance?: {
          id: string
          joined: boolean
        }
        employmentInsurance?: {
          id: string
          joined: boolean
        }
        currentOffice: {
          id: string
          healthInsuranceType: 'kyokai' | 'its' | 'other'
        }
        pastChildcareProcedureStatuses: {id: string}[]
      }
      childcareMethodSelect?: {
        id: string
      }
    }
  }
}

const useFlowQuery: UseFlowQuery<FlowQueryData> = ({procedureStatusId}) => {
  return useQuery<FlowQueryData>(START_CHILDCARE_FLOW_QUERY, {
    variables: {procedureStatusId},
    fetchPolicy: 'network-only',
  })
}

const makeFlow: FlowGenerator<FlowQueryData> = function* (data, _flowStatus, flowMode) {
  const procedureStatus = data.client.procedureStatus
  const employee = procedureStatus.employee
  const healthInsuranceJoined = employee.healthInsurance?.joined
  const welfarePensionInsuranceJoined = employee.welfarePensionInsurance?.joined
  const employmentInsuranceJoined = employee.employmentInsurance?.joined
  const healthInsuranceType = employee.currentOffice.healthInsuranceType

  yield* maybeYieldChildcareMethodSelect({
    flowMode,
    pastChildcareCount: employee.pastChildcareProcedureStatuses.length,
    methodSelectExists: !!procedureStatus.childcareMethodSelect,
  })

  // ChildcareInfoForm は mode='new' でも必要なため常に yield
  yield 'ChildcareInfo'

  if (healthInsuranceType === 'kyokai' || healthInsuranceType === 'other') {
    if (healthInsuranceJoined || welfarePensionInsuranceJoined) {
      yield 'R507'
    }
    if (employmentInsuranceJoined) {
      yield 'R509'
    }
  } else if (healthInsuranceType === 'its') {
    if (healthInsuranceJoined || welfarePensionInsuranceJoined) {
      yield 'R508'
    }
    if (employmentInsuranceJoined) {
      yield 'R509'
    }
  }

  yield 'Attachments'
}

export const StartChildcareFlow = () => (
  <Flow procedureContext="mypage" makeFlow={makeFlow} useFlowQuery={useFlowQuery} />
)
